import React, { useState, useEffect } from "react";
import { gql, useQuery } from "@apollo/client";
import {
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiDownload,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiChevronsLeft,
  FiChevronsRight,
  FiTrendingUp,
  FiAlertCircle,
  FiCalendar,
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiBriefcase
} from "react-icons/fi";
import "../styles/ServiceBookingStatus.css";

// ---------------------------------------------------------
// GRAPHQL QUERY
// ---------------------------------------------------------
const GET_ALL_CONTACTS = gql`
  query GetAllContacts($page: Int, $limit: Int, $sortBy: String) {
    getAllContacts(page: $page, limit: $limit, sortBy: $sortBy) {
      pages
      data {
       
        name
        email
        phone
        location
        status
        date
        sellerId
      }
    }
  }
`;

// ---------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------
export default function ServiceBookingStatus() {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [sellerFilter, setSellerFilter] = useState("");
  const [sortKey, setSortKey] = useState("-date");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);

  const { data, loading, error, refetch } = useQuery(GET_ALL_CONTACTS, {
    variables: { page: currentPage, limit: itemsPerPage, sortBy: sortKey },
    fetchPolicy: "network-only",
  });

  // Process and store bookings
  useEffect(() => {
    if (data?.getAllContacts?.data) {
      const processed = data.getAllContacts.data.map(b => ({
        ...b,
        status: b.status?.toLowerCase()
      }));
      setBookings(processed);
      setFilteredBookings(processed);
    }
  }, [data]);

  // Apply filters
  useEffect(() => {
    let results = [...bookings];
    
    // Search term
    if (query) {
      const term = query.toLowerCase();
      results = results.filter(b => 
        b.name?.toLowerCase().includes(term) ||
        b.email?.toLowerCase().includes(term) ||
        (b.phone || "").toLowerCase().includes(term) ||
        b.location?.toLowerCase().includes(term) ||
        b.status?.toLowerCase().includes(term) ||
        (b.sellerId || "").toLowerCase().includes(term)
      );
    }
    
    // Status filter
    if (statusFilter !== "all") {
      results = results.filter(b => b.status === statusFilter);
    }
    
    // Date filter
    if (dateFilter) {
      results = results.filter(b => {
        const bookingDate = parseDate(b.date);
        if (!bookingDate) return false;
        const filterDate = new Date(dateFilter);
        return bookingDate.toDateString() === filterDate.toDateString();
      });
    }
    
    // Seller filter
    if (sellerFilter) {
      results = results.filter(b => 
        (b.sellerId || "").toLowerCase().includes(sellerFilter.toLowerCase())
      );
    }
    
    setFilteredBookings(results);
  }, [query, statusFilter, dateFilter, sellerFilter, bookings]);

  const parseDate = (value) => {
    if (!value) return null;
    if (!isNaN(value)) {
      if (value.toString().length === 10) return new Date(value * 1000);
      return new Date(Number(value));
    }
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatDate = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "N/A";
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateValue) => {
    const date = parseDate(dateValue);
    if (!date) return "N/A";
    return date.toLocaleString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Statistics
  const stats = {
    total: bookings.length,
    pending: bookings.filter(b => b.status === "pending").length,
    confirmed: bookings.filter(b => b.status === "confirmed").length,
    completed: bookings.filter(b => b.status === "completed").length,
    cancelled: bookings.filter(b => b.status === "cancelled").length
  };

  // Pagination
  const totalPages = data?.getAllContacts?.pages || 1;

  const resetFilters = () => {
    setQuery("");
    setStatusFilter("all");
    setDateFilter("");
    setSellerFilter("");
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    const headers = [
      "Name",
      "Email",
      "Phone",
      "Location",
      "Status",
      "Date",
      "Seller ID"
    ];

    const rows = filteredBookings.map(b => [
      `"${b.name || ""}"`,
      `"${b.email || ""}"`,
      `"${b.phone || ""}"`,
      `"${b.location || ""}"`,
      b.status?.charAt(0).toUpperCase() + b.status?.slice(1),
      formatDate(b.date),
      `"${b.sellerId || ""}"`
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    link.setAttribute("href", url);
    link.setAttribute("download", `service-bookings-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = "hidden";
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return (
    <div className="booking-loading">
      <div className="premium-spinner"></div>
      <p>Loading service bookings...</p>
    </div>
  );
  
  if (error) return (
    <div className="booking-error">
      <div className="error-icon">
        <FiAlertCircle />
      </div>
      <p>Error loading data</p>
      <button onClick={() => refetch()} className="retry-btn">
        Try Again
      </button>
    </div>
  );

  return (
    <div className="booking-container premium">
      {/* Premium Header */}
      <div className="premium-header">
        <div className="header-main">
          <h1 className="premium-title">
            <FiTrendingUp className="title-icon" />
            Service Bookings
          </h1>
          <p className="premium-subtitle">Live Data from Backend</p>
        </div>
        
        <div className="header-stats">
          <div className="stat-mini total">
            <div className="stat-mini-value">{stats.total}</div>
            <div className="stat-mini-label">Total</div>
          </div>
          <div className="stat-mini pending">
            <div className="stat-mini-value">{stats.pending}</div>
            <div className="stat-mini-label">Pending</div>
          </div>
          <div className="stat-mini confirmed">
            <div className="stat-mini-value">{stats.confirmed}</div>
            <div className="stat-mini-label">Confirmed</div>
          </div>
          <div className="stat-mini completed">
            <div className="stat-mini-value">{stats.completed}</div>
            <div className="stat-mini-label">Completed</div>
          </div>
          <div className="stat-mini cancelled">
            <div className="stat-mini-value">{stats.cancelled}</div>
            <div className="stat-mini-label">Cancelled</div>
          </div>
        </div>
        
        <div className="header-controls">
          <button className="premium-btn export" onClick={exportToCSV}>
            <FiDownload /> Export
          </button>
          <button className="premium-btn refresh" onClick={() => refetch()}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {/* Premium Filter Bar */}
      <div className="premium-filter-bar">
        <div className="search-wrapper">
          <div className="search-icon">
            <FiSearch />
          </div>
          <input
            type="text"
            placeholder="Search name, phone, email, sellerId..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="premium-search"
          />
          {query && (
            <button className="clear-btn" onClick={() => setQuery("")}>
              <FiX />
            </button>
          )}
        </div>
        
        <div className="filter-controls">
          <button 
            className={`filter-btn ${showFilters ? 'active' : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiFilter /> Filter
          </button>
          <div className="page-control">
            <select 
              value={itemsPerPage} 
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="premium-select"
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="premium-advanced-filters">
          <div className="filter-row">
            <div className="filter-col">
              <label>Status</label>
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)}
                className="premium-select"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            <div className="filter-col">
              <label>Date</label>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="premium-select"
              />
            </div>
            
            <div className="filter-col">
              <label>Seller ID</label>
              <input
                type="text"
                placeholder="Seller ID"
                value={sellerFilter}
                onChange={(e) => setSellerFilter(e.target.value)}
                className="premium-select"
              />
            </div>
            
            <div className="filter-col">
              <label>Sort By</label>
              <select 
                value={sortKey} 
                onChange={(e) => {
                  setSortKey(e.target.value);
                  setCurrentPage(1);
                }}
                className="premium-select"
              >
                <option value="-date">Newest First</option>
                <option value="date">Oldest First</option>
                <option value="name">Name A-Z</option>
                <option value="-name">Name Z-A</option>
              </select>
            </div>
            
            <div className="filter-col">
              <button className="reset-filters" onClick={resetFilters}>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Premium Table */}
      <div className="premium-table-container">
        <div className="table-info-bar">
          <div className="table-count">
            {filteredBookings.length} bookings
          </div>
          <div className="table-navigation">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredBookings.length)}-{Math.min(currentPage * itemsPerPage, filteredBookings.length)} of {filteredBookings.length}
          </div>
        </div>
        
        <div className="table-scroll-wrapper">
          <table className="premium-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Status</th>
                <th>Date</th>
                <th>Seller ID</th>
              </tr>
            </thead>
            
            <tbody>
              {filteredBookings.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-state">
                    <FiAlertCircle />
                    <p>No service bookings found</p>
                  </td>
                </tr>
              ) : (
                filteredBookings.map((booking, index) => (
                  <tr 
                    key={booking.id || index} 
                    className="booking-row"
                    onClick={() => setSelectedBooking(booking)}
                  >
                    <td className="row-number">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td>
                      <div className="customer-info">
                        <div className="customer-name">
                          <FiUser /> {booking.name || "—"}
                        </div>
                        <div className="customer-email">
                          <FiMail /> {booking.email || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="contact-info">
                        <div className="phone">
                          <FiPhone /> {booking.phone || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="location-info">
                        <div className="location">
                          <FiMapPin /> {booking.location || "—"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="status-cell">
                        <span className={`status-badge ${booking.status}`}>
                          {booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1) || "—"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="date-info">
                        <div className="date">
                          <FiCalendar /> {formatDate(booking.date)}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="seller-info">
                        <div className="seller-id">
                          <FiBriefcase /> {booking.sellerId || "—"}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Premium Pagination */}
        {totalPages > 1 && (
          <div className="premium-pagination">
            <div className="pagination-info">
              Page {currentPage} of {totalPages}
            </div>
            <div className="pagination-nav">
              <button 
                className="nav-btn first"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                <FiChevronsLeft />
              </button>
              <button 
                className="nav-btn prev"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <FiChevronLeft />
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return pageNum <= totalPages ? (
                  <button
                    key={pageNum}
                    className={`nav-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                ) : null;
              })}
              
              <button 
                className="nav-btn next"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                <FiChevronRight />
              </button>
              <button 
                className="nav-btn last"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                <FiChevronsRight />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Premium Modal */}
      {selectedBooking && (
        <div className="premium-modal-overlay" onClick={() => setSelectedBooking(null)}>
          <div className="premium-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Booking Details</h3>
              <button className="modal-close" onClick={() => setSelectedBooking(null)}>
                <FiX />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="detail-section">
                <div className="detail-row">
                  <span className="detail-label">Customer</span>
                  <span className="detail-value">
                    <FiUser /> {selectedBooking.name}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email</span>
                  <span className="detail-value">
                    <FiMail /> {selectedBooking.email}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Phone</span>
                  <span className="detail-value">
                    <FiPhone /> {selectedBooking.phone || "—"}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Location</span>
                  <span className="detail-value">
                    <FiMapPin /> {selectedBooking.location}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className={`detail-value status ${selectedBooking.status}`}>
                    {selectedBooking.status?.charAt(0).toUpperCase() + selectedBooking.status?.slice(1)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date</span>
                  <span className="detail-value">
                    <FiCalendar /> {formatDateTime(selectedBooking.date)}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Seller ID</span>
                  <span className="detail-value">
                    <FiBriefcase /> {selectedBooking.sellerId || "—"}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="modal-action-btn" onClick={() => setSelectedBooking(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}